import { ReactNode, useState } from 'react'
import { CheckmarkIcon, CrossIcon } from '@honestica/curecall-ui'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { TInteropEntity, TInteropEntityTree } from './interop-entities.util'
import { InteropValueComponent } from './interop-value.component'
import { InteropLogActionsComponent } from './interop-log-actions.component'
import { TPatientInteropLog } from './interop-log.type'
import { TPatient } from 'src/types/patient'
import { TProject } from 'src/types/project'
import { twMerge } from 'src/utils/tw-merge.util'
import {
  formatAllDatesWithHoursInObject,
  getFormattedDateWithHours,
  getFormattedPeriod,
} from 'src/utils/date-formatter.util'
import { useTimezonePreference } from 'src/providers/timezone-preference.provider'
import TimezoneAwareTime from 'src/components/timezone-aware-time/timezone-aware-time.component'

const ChevronIcon = ({ isOpen }: Readonly<{ isOpen: boolean }>) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={twMerge(
      'c-h-4 c-w-4 c-flex-none c-text-gray-400 c-transition-transform c-duration-200',
      isOpen && 'c-rotate-90',
    )}
  >
    <path
      fillRule="evenodd"
      d="M7.2 4.8a1 1 0 0 1 1.4 0l4.5 4.5a1 1 0 0 1 0 1.4l-4.5 4.5a1 1 0 1 1-1.4-1.4L11 10 7.2 6.2a1 1 0 0 1 0-1.4Z"
      clipRule="evenodd"
    />
  </svg>
)

function FieldRow({ field, entity }: Readonly<{ field: string; entity: TInteropEntity }>) {
  const { t } = useTranslation()
  const { displayTimezone } = useTimezonePreference()
  const [isOpen, setIsOpen] = useState(false)
  const changes = entity.fieldHistories[field] ?? []
  const hasHistory = changes.length > 1
  const formatValue = (value: unknown) => formatAllDatesWithHoursInObject(t, value, displayTimezone)

  return (
    <li className="c-py-2 c-border-b c-border-gray-100 last:c-border-b-0">
      <div className="c-flex c-gap-3 c-items-start">
        <span className="c-w-1/3 c-flex-none c-text-xs c-font-medium c-text-gray-500 c-break-words c-pt-0.5">
          {field}
        </span>
        <div className="c-flex-1 c-min-w-0 c-text-gray-900">
          <InteropValueComponent value={formatValue(entity.data[field])} />
        </div>
        {hasHistory && (
          <button
            type="button"
            aria-expanded={isOpen}
            onClick={() => setIsOpen(!isOpen)}
            className={twMerge(
              'c-flex-none c-flex c-items-center c-gap-1 c-rounded-full c-border c-px-2 c-py-0.5 c-text-xs c-font-medium c-transition-colors',
              isOpen
                ? 'c-bg-blue-600 c-border-blue-600 c-text-white'
                : 'c-bg-blue-50 c-border-blue-200 c-text-blue-700 hover:c-bg-blue-100',
            )}
          >
            <span className="c-h-1.5 c-w-1.5 c-rounded-full c-bg-current" />
            {t('components.patient.interop-logs.entities.field-changes', { count: changes.length - 1 })}
          </button>
        )}
      </div>
      {isOpen && (
        <ol className="c-list-none c-m-0 c-mt-3 c-ml-2 c-p-0 c-border-l-2 c-border-blue-100">
          {changes
            .slice()
            .reverse()
            .map((change, changeIndex) => (
              <li key={change.interopLog.id} className="c-relative c-pl-4 c-pb-3 last:c-pb-0">
                <span
                  className={twMerge(
                    'c-absolute c--left-[5px] c-top-1.5 c-h-2 c-w-2 c-rounded-full c-ring-2 c-ring-white',
                    changeIndex === 0 ? 'c-bg-blue-600' : 'c-bg-gray-300',
                  )}
                />
                <div className="c-text-xs c-text-gray-500">
                  <TimezoneAwareTime
                    date={change.interopLog.payload?.timestamp ?? change.interopLog.createdAt}
                    formatter="dateWithHours"
                    displaySeconds
                  />
                  <span className="c-ml-2 c-rounded c-bg-gray-100 c-px-1.5 c-py-0.5 c-font-mono">
                    #{change.interopLog.id}
                  </span>
                </div>
                <div
                  className={twMerge(
                    'c-mt-1',
                    changeIndex === 0 ? 'c-text-gray-900' : 'c-text-gray-500 c-line-through',
                  )}
                >
                  <InteropValueComponent value={formatValue(change.value)} />
                </div>
              </li>
            ))}
        </ol>
      )}
    </li>
  )
}

const NO_MAPPING_ERROR = 'No mapping found for consultation data'

function MappingChip({ interopLog }: Readonly<{ interopLog: TPatientInteropLog }>) {
  const realError = interopLog.errors?.find((error) => error.error !== NO_MAPPING_ERROR)
  if (realError) {
    return (
      <span
        title={realError.error}
        className="c-flex c-flex-none c-items-center c-gap-1 c-rounded-full c-bg-red-50 c-px-2 c-py-0.5 c-text-xs c-font-medium c-text-red-700"
      >
        <CrossIcon className="c-h-3 c-w-3" />
        {interopLog.InteropMapping?.description || realError.error}
      </span>
    )
  }
  if (interopLog.InteropMappingId > 0) {
    return (
      <span className="c-flex c-flex-none c-items-center c-gap-1 c-rounded-full c-bg-emerald-50 c-px-2 c-py-0.5 c-text-xs c-font-medium c-text-emerald-700">
        <CheckmarkIcon className="c-h-3 c-w-3" />
        {interopLog.InteropMapping.description}
      </span>
    )
  }
  return null
}

function EntityCard({
  entity,
  badge,
  title,
  subtitle,
  accentClassName,
  badgeClassName,
  patient,
  project,
  children,
}: Readonly<{
  entity: TInteropEntity
  badge: string
  title: string
  subtitle?: ReactNode
  accentClassName: string
  badgeClassName: string
  patient: Pick<TPatient, 'id'>
  project: TProject
  children?: ReactNode
}>) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { displayTimezone } = useTimezonePreference()
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div
      className={twMerge(
        'c-mb-3 c-overflow-hidden c-rounded-xl c-border c-border-gray-200 c-border-l-4 c-bg-white c-shadow-sm c-transition-shadow hover:c-shadow-md',
        accentClassName,
      )}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        className="c-w-full c-flex c-items-center c-gap-3 c-px-3 c-py-2.5 c-text-left c-bg-transparent c-border-0 hover:c-bg-gray-50"
      >
        <ChevronIcon isOpen={isOpen} />
        <span
          aria-hidden="true"
          className={twMerge(
            'c-flex c-h-7 c-w-7 c-flex-none c-items-center c-justify-center c-rounded-lg c-text-xs c-font-bold',
            badgeClassName,
          )}
        >
          {badge}
        </span>
        <span className="c-flex-1 c-min-w-0">
          <span className="c-block c-truncate c-font-semibold c-text-gray-900">{title}</span>
          <span className="c-block c-text-xs c-text-gray-500">{subtitle}</span>
        </span>
        <MappingChip interopLog={entity.latestLog} />
        <span className="c-flex-none c-text-xs c-text-gray-500">
          {t('components.patient.interop-logs.entities.last-update', {
            date: getFormattedDateWithHours(
              t,
              entity.latestLog.payload?.timestamp ?? entity.latestLog.createdAt,
              true,
              displayTimezone,
            ),
          })}
        </span>
        <span className="c-flex-none c-rounded-full c-bg-gray-100 c-px-2 c-py-0.5 c-text-xs c-font-medium c-text-gray-600">
          {t('components.patient.interop-logs.entities.versions', { count: entity.history.length + 1 })}
        </span>
      </button>
      {isOpen && (
        <>
          <ul className="c-list-none c-m-0 c-mx-3 c-mb-3 c-rounded-lg c-bg-gray-50 c-px-3 c-py-1">
            {Object.keys(entity.data).map((field) => (
              <FieldRow key={field} field={field} entity={entity} />
            ))}
          </ul>
          <div className="c-mx-3 c-mb-3">
            <InteropLogActionsComponent
              interopLog={entity.latestLog}
              patient={patient}
              project={project}
              onReplayed={() =>
                queryClient.invalidateQueries({ queryKey: ['patient-interop-logs', project.id, patient.id] })
              }
            />
          </div>
        </>
      )}
      {children}
    </div>
  )
}

function VisitPeriod({ visit }: Readonly<{ visit: TInteropEntity }>) {
  const { t } = useTranslation()
  const { displayTimezone } = useTimezonePreference()
  return (
    <span>
      {getFormattedPeriod(
        t,
        visit.data.admissionTime as string | undefined,
        visit.data.dischargeTime as string | undefined,
        displayTimezone,
      )}
    </span>
  )
}

function ConsultationCard({
  consultation,
  patient,
  project,
}: Readonly<{ consultation: TInteropEntity; patient: Pick<TPatient, 'id'>; project: TProject }>) {
  const { t } = useTranslation()
  const name =
    (consultation.data.prettyName as string | undefined) || (consultation.data.code as string | undefined)
  return (
    <EntityCard
      entity={consultation}
      badge="C"
      title={
        name
          ? t('components.patient.interop-logs.entities.consultation', { name })
          : t('components.patient.interop-logs.entities.consultation-unnamed')
      }
      subtitle={
        <>
          <span className="c-font-mono">{consultation.remoteId}</span>
          <span className="c-mx-1.5">·</span>
          <TimezoneAwareTime date={consultation.data.date as string} formatter="dateWithHours" />
        </>
      }
      accentClassName="c-border-l-amber-400"
      badgeClassName="c-bg-amber-100 c-text-amber-700"
      patient={patient}
      project={project}
    />
  )
}

export function InteropEntityTreeComponent({
  tree,
  patient,
  project,
}: Readonly<{ tree: TInteropEntityTree; patient: Pick<TPatient, 'id'>; project: TProject }>) {
  const { t } = useTranslation()

  return (
    <div>
      {tree.visits.map(({ visit, consultations }) => (
        <EntityCard
          key={visit.remoteId}
          entity={visit}
          badge="V"
          title={t('components.patient.interop-logs.entities.visit', {
            ufCare: (visit.data.ufCareId as string | undefined) || '—',
            ufMed: (visit.data.ufMedId as string | undefined) || '—',
          })}
          subtitle={
            <>
              <span className="c-font-mono">{visit.remoteId}</span>
              <span className="c-mx-1.5">·</span>
              <VisitPeriod visit={visit} />
            </>
          }
          accentClassName="c-border-l-emerald-500"
          badgeClassName="c-bg-emerald-100 c-text-emerald-700"
          patient={patient}
          project={project}
        >
          {consultations.length > 0 && (
            <div className="c-ml-8 c-mr-3 c-mb-1 c-border-l-2 c-border-gray-200 c-pl-4 c-pt-1">
              {consultations.map((consultation) => (
                <ConsultationCard
                  key={consultation.remoteId}
                  consultation={consultation}
                  patient={patient}
                  project={project}
                />
              ))}
            </div>
          )}
        </EntityCard>
      ))}
      {tree.consultationsWithoutVisit.length > 0 && (
        <>
          <h5 className="c-mt-6 c-mb-2 c-text-xs c-font-semibold c-uppercase c-tracking-wide c-text-gray-500">
            {t('components.patient.interop-logs.entities.without-visit')}
          </h5>
          {tree.consultationsWithoutVisit.map((consultation) => (
            <ConsultationCard
              key={consultation.remoteId}
              consultation={consultation}
              patient={patient}
              project={project}
            />
          ))}
        </>
      )}
    </div>
  )
}
